import { queryOptions, useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { ArrowLeft, CalendarDays, Clock, Users } from "lucide-react";

import { DisposicionSitio } from "@/components/medam/sitio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatearFecha } from "@/lib/catalogos";
import { obtenerPosicionPublica } from "@/lib/posiciones.functions";

const posicionQuery = (id: string) =>
  queryOptions({
    queryKey: ["posicion", id],
    queryFn: () => obtenerPosicionPublica({ id }),
  });

export const Route = createFileRoute("/posiciones/$id/")({
  loader: async ({ context, params }) => {
    const posicion = await context.queryClient.ensureQueryData(posicionQuery(params.id));
    if (!posicion) throw notFound();
    return { nombre: posicion.nombre, objetivo: posicion.objetivo };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Posición no disponible | MEDAM" }, { name: "robots", content: "noindex" }],
      };
    }
    const titulo = `${loaderData.nombre} | Posiciones MEDAM`;
    return {
      meta: [
        { title: titulo },
        { name: "description", content: loaderData.objetivo.slice(0, 155) },
        { property: "og:title", content: titulo },
        { property: "og:description", content: loaderData.objetivo.slice(0, 155) },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  errorComponent: ({ error }) => (
    <DisposicionSitio>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-2xl">No pudimos cargar la posición</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
      </div>
    </DisposicionSitio>
  ),
  notFoundComponent: () => (
    <DisposicionSitio>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-2xl">Esta posición ya no está disponible</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Revisa las demás posiciones publicadas por MEDAM.
        </p>
        <Button asChild className="mt-6">
          <Link to="/posiciones">Ver posiciones</Link>
        </Button>
      </div>
    </DisposicionSitio>
  ),
  component: DetallePosicion,
});

function DetallePosicion() {
  const { id } = Route.useParams();
  const { data: posicion, isLoading, isError, error } = useQuery(posicionQuery(id));

  if (isLoading) {
    return (
      <DisposicionSitio>
        <div className="mx-auto max-w-3xl px-4 py-24 text-center">
          <h1 className="font-display text-2xl">Cargando posición...</h1>
        </div>
      </DisposicionSitio>
    );
  }

  if (isError) {
    return (
      <DisposicionSitio>
        <div className="mx-auto max-w-3xl px-4 py-24 text-center">
          <h1 className="font-display text-2xl">No pudimos cargar la posición</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {error instanceof Error ? error.message : "Intenta nuevamente en unos momentos."}
          </p>
        </div>
      </DisposicionSitio>
    );
  }

  if (!posicion) return null;

  const vencida = Boolean(
    posicion.fecha_limite && posicion.fecha_limite < new Date().toISOString().slice(0, 10),
  );

  return (
    <DisposicionSitio>
      <section className="superficie-institucional border-b border-border/60">
        <div className="mx-auto w-full max-w-4xl px-4 py-12 sm:px-6">
          <Link
            to="/posiciones"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" /> Volver a posiciones
          </Link>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <Badge variant="secondary">{posicion.area}</Badge>
            {posicion.subarea ? <Badge variant="outline">{posicion.subarea}</Badge> : null}
            <Badge variant="outline">{posicion.turno}</Badge>
            {posicion.tipo_jornada ? (
              <Badge variant="outline">{posicion.tipo_jornada}</Badge>
            ) : null}
          </div>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl">{posicion.nombre}</h1>
          <div className="mt-5 grid gap-3 text-sm text-muted-foreground sm:grid-cols-3">
            <span className="flex items-center gap-2">
              <Clock className="size-4 text-primary" /> {posicion.horario || "Horario por definir"}
            </span>
            <span className="flex items-center gap-2">
              <Users className="size-4 text-primary" /> {posicion.vacantes}{" "}
              {posicion.vacantes === 1 ? "posición" : "posiciones"}
            </span>
            <span className="flex items-center gap-2">
              <CalendarDays className="size-4 text-primary" />{" "}
              {formatearFecha(posicion.fecha_limite)}
            </span>
          </div>
          <div className="mt-8">
            {vencida ? (
              <p className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
                La fecha límite para postularse a esta posición ya concluyó.
              </p>
            ) : (
              <Button asChild size="lg" className="h-12 px-6 text-base">
                <Link to="/posiciones/$id/postular" params={{ id: posicion.id }}>
                  Quiero postularme
                </Link>
              </Button>
            )}
          </div>
        </div>
      </section>

      <article className="mx-auto w-full max-w-4xl space-y-10 px-4 py-12 sm:px-6">
        <Bloque titulo="Objetivo del puesto" texto={posicion.objetivo} />
        <Bloque titulo="Descripción de la posición" texto={posicion.descripcion} />
        <BloqueLista titulo="Actividades principales" elementos={posicion.actividades} />
        <Bloque titulo="Perfil que buscamos" texto={posicion.perfil} />
        <div className="grid gap-6 sm:grid-cols-2">
          <Bloque titulo="Escolaridad requerida" texto={posicion.escolaridad} />
          <Bloque titulo="Experiencia requerida" texto={posicion.experiencia} />
        </div>
        <BloqueLista titulo="Conocimientos técnicos" elementos={posicion.conocimientos} />
        <BloqueLista titulo="Competencias esperadas" elementos={posicion.competencias} />
        <BloqueLista
          titulo="Documentos indispensables"
          elementos={posicion.documentos_indispensables}
        />

        {!vencida ? (
          <div className="rounded-2xl border border-primary/20 bg-secondary/50 p-6 text-center">
            <h2 className="font-display text-xl">¿Te interesa esta posición?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Fecha límite para postularse: {formatearFecha(posicion.fecha_limite)}
            </p>
            <Button asChild size="lg" className="mt-5">
              <Link to="/posiciones/$id/postular" params={{ id: posicion.id }}>
                Quiero postularme
              </Link>
            </Button>
          </div>
        ) : null}
      </article>
    </DisposicionSitio>
  );
}

function Bloque({ titulo, texto }: { titulo: string; texto: string }) {
  if (!texto) return null;
  return (
    <section>
      <h2 className="font-display text-xl">{titulo}</h2>
      <p className="mt-2 whitespace-pre-line leading-relaxed text-muted-foreground">{texto}</p>
    </section>
  );
}

function BloqueLista({ titulo, elementos }: { titulo: string; elementos: string[] }) {
  if (!elementos?.length) return null;
  return (
    <section>
      <h2 className="font-display text-xl">{titulo}</h2>
      <ul className="mt-3 space-y-2">
        {elementos.map((elemento) => (
          <li key={elemento} className="flex gap-3 text-muted-foreground">
            <span className="mt-2 size-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />
            <span className="leading-relaxed">{elemento}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
